import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Modal, ModalTitle } from '@runtime/components/ui/Modal'
import { Button } from '@runtime/components/ui/Button'
import { useI18n } from '@runtime/core/i18n/i18n'
import PuzzleHelpModal from '@/routes/puzzle/components/PuzzleHelpModal'
import OnlineHelpModal from '@/routes/online/components/OnlineHelpModal'
import TabbedCharacterHelpModal from '@/features/help/components/TabbedCharacterHelpModal'
import { strategyHelpTabs } from '@/routes/strategy/components/strategyHelpTabs'
import { trapDialogFocus } from '@/shared/a11y/trapDialogFocus'
import styles from './CoachRulesModal.module.scss'

export type CoachRulesKind = 'strategy' | 'puzzle' | 'online'

export default function CoachRulesModal({
  kind,
  open,
  onClose,
  content,
}: {
  kind?: CoachRulesKind
  open: boolean
  onClose: () => void
  content?: ReactNode
}) {
  const { t } = useI18n()
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        onClose()
      }
      trapDialogFocus(event, root.current)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose, open])

  const modal = content ? (
    <Modal open={open} onClose={onClose} contentClassName={styles.content}>
      <ModalTitle>{t('coach.localActions.screenRules')}</ModalTitle>
      <section
        className={styles.body}
        tabIndex={0}
        aria-label={t('coach.localActions.screenRules')}
      >
        {content}
      </section>
      <Button autoFocus onClick={onClose}>
        {t('common.close')}
      </Button>
    </Modal>
  ) : kind === 'puzzle' ? (
    <PuzzleHelpModal open={open} onClose={onClose} />
  ) : kind === 'online' ? (
    <OnlineHelpModal open={open} onClose={onClose} />
  ) : (
    <TabbedCharacterHelpModal
      open={open}
      onClose={onClose}
      character="coach"
      title={t('strategy.help.how.title')}
      tabs={strategyHelpTabs(t)}
    />
  )
  return createPortal(
    <div ref={root} data-testid="coach-rules-dialog">
      {modal}
    </div>,
    document.body,
  )
}
